import React, { useState, useMemo } from 'react';
import { Checkbox, Grid } from '@mui/material';
import { PraksisstederForAktivitetskravDto } from '../api/generated/studiested-generated-api';
import * as css from './practiceplaceswithagreements.module.scss';
import { useIntl } from 'react-intl';
import translations from './practiceplaceswithagreements.uha.i18n';
import classNames from 'classnames';
import Spacer from '../components/spacer/spacer';
import { getLeafNodes, isLeafNode } from '../api/praksisstedNodes';
import Icon, { IconType } from '../components/icon';

type Node = PraksisstederForAktivitetskravDto;

enum TopOrMiddleNodeState {
  Indeterminate,
  Checked,
  Unchecked,
}

const getTopOrMiddleNodeState = (
  nodes: Node[],
  checkboxItems: Map<string, boolean>,
) => {
  /* Return the state of a node with the given child 'nodes' */

  const leafNodes = getLeafNodes(nodes);
  const checkedLeafNodes = leafNodes.filter((node) =>
    checkboxItems.get(node.id),
  );

  if (checkedLeafNodes.length > 0) {
    return leafNodes.length === checkedLeafNodes.length
      ? TopOrMiddleNodeState.Checked
      : TopOrMiddleNodeState.Indeterminate;
  }

  return TopOrMiddleNodeState.Unchecked;
};

interface StederProps {
  praksissteder: Node[];
  updatePraksisstedIdsWithKravOppfylt: (steder: Map<string, boolean>) => number;
}

const PraksisstederWithAvtale: React.FC<
  React.PropsWithChildren<StederProps>
> = ({ praksissteder, updatePraksisstedIdsWithKravOppfylt }) => {
  const { formatMessage } = useIntl();
  const labels = {
    numPlaces: formatMessage({ id: translations.numPlaces.id }),
    practicePlacesWithAgreement: formatMessage({
      id: translations.practicePlacesWithAgreement.id,
    }),
    meetsRequirement: formatMessage({ id: translations.meetsRequirement.id }),
  };

  const countInitialNumPlacesWithKravOppfylt = () => {
    return getLeafNodes(praksissteder).filter((n) => n.oppfyllerKrav).length;
  };

  const [numPlaces, setNumPlaces] = useState(
    countInitialNumPlacesWithKravOppfylt(),
  );
  const updatePraksisstedIdsWithKravOppfyltWrapper = (
    steder: Map<string, boolean>,
  ) => {
    setNumPlaces(updatePraksisstedIdsWithKravOppfylt(steder));
  };
  return (
    <div className={css.page}>
      <span className={css.numPlacesText}>
        {labels.numPlaces}
        <span className={css.numPlaces} id="numPlaces">
          {numPlaces}
        </span>
      </span>
      <Grid container={true}>
        <Grid container={true} className={css.praksisstederHeader}>
          <Grid item={true} xs={9}>
            {labels.practicePlacesWithAgreement}
          </Grid>
          <Grid item={true} xs={2}>
            {labels.meetsRequirement}
          </Grid>
        </Grid>
        {praksissteder.map((node, index) => (
          <TopNode
            key={`${node.id}-top-${index}`}
            praksissted={node}
            updatePraksisstedIdsWithKravOppfylt={
              updatePraksisstedIdsWithKravOppfyltWrapper
            }
          />
        ))}
      </Grid>
    </div>
  );
};

interface TopNodeProps {
  praksissted: Node;
  updatePraksisstedIdsWithKravOppfylt: (steder: Map<string, boolean>) => void;
}

const TopNode: React.FC<React.PropsWithChildren<TopNodeProps>> = ({
  praksissted,
  updatePraksisstedIdsWithKravOppfylt,
}) => {
  const topNodeHasChildNodes = praksissted.undernoder.length > 0;
  const initCheckedItems = (nodes: Node[]) => {
    return new Map(nodes.map((node) => [node.id, node.oppfyllerKrav]));
  };
  // If zero 'undernoder' then the top node also is the leaf node.
  const leafNodes = useMemo(
    () =>
      topNodeHasChildNodes
        ? getLeafNodes(praksissted.undernoder)
        : [praksissted],
    [topNodeHasChildNodes, praksissted],
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [checkboxItems, setCheckedItems] = useState<Map<string, boolean>>(
    initCheckedItems(leafNodes),
  );
  const checkboxState = useMemo(
    () => getTopOrMiddleNodeState(leafNodes, checkboxItems),
    [leafNodes, checkboxItems],
  );
  const rowIconType = useMemo<IconType>(
    () => (isExpanded ? IconType.CloseRowTriangle : IconType.OpenRow),
    [isExpanded],
  );

  const onCheckedItemsUpdated = (items: Map<string, boolean>) => {
    setCheckedItems(items);
    updatePraksisstedIdsWithKravOppfylt(items);
  };

  // Update single checkbox for one leaf node
  const updateCheckedItems = (id: string, checked: boolean) => {
    const items = new Map(checkboxItems);
    items.set(id, checked);
    onCheckedItemsUpdated(items);
  };

  // Checkbox for top level node was clicked, update all checkboxes for corresponding leaf nodes
  const updateSubCheckedItems = (parentNode: Node, checked: boolean) => {
    onCheckedItemsUpdated(
      parentNode.undernoder.length > 0
        ? new Map(
            getLeafNodes(parentNode.undernoder).map((node) => [
              node.id,
              checked,
            ]),
          )
        : new Map([[parentNode.id, checked]]),
    );
  };

  const handleRowToggle = () => {
    topNodeHasChildNodes && setIsExpanded(!isExpanded);
  };

  return (
    <>
      <Grid
        container={true}
        className={classNames(
          css.topLevelNodeRow,
          topNodeHasChildNodes && css.hasChildNodes,
        )}
        id={praksissted.id}
      >
        <Grid item={true} xs={9} onClick={handleRowToggle} className={css.node}>
          {topNodeHasChildNodes && (
            <Icon
              type={rowIconType}
              htmlId={praksissted.id}
              className={css.toggleExpand}
            />
          )}
          <div className={classNames(css.praksisstedName, 'praksisstedName')}>
            {praksissted.navn}
          </div>
        </Grid>
        <Grid item={true} xs={2}>
          <Checkbox
            color="secondary"
            indeterminate={checkboxState === TopOrMiddleNodeState.Indeterminate}
            checked={checkboxState === TopOrMiddleNodeState.Checked}
            onChange={(e) =>
              updateSubCheckedItems(praksissted, e.target.checked)
            }
          />
        </Grid>
      </Grid>
      {isExpanded &&
        praksissted.undernoder.map((node) => (
          <MiddleOrLeafNode
            key={node.id}
            node={node}
            updateCheckedItems={updateCheckedItems}
            updateSubCheckedItems={updateSubCheckedItems}
            checkboxItems={checkboxItems}
            indent={1}
          />
        ))}
    </>
  );
};

interface MiddleNodeProps {
  node: Node;
  updateCheckedItems: (id: string, checked: boolean) => void;
  updateSubCheckedItems: (node: Node, checked: boolean) => void;
  checkboxItems: Map<string, boolean>;
  indent: number;
}

const MiddleOrLeafNode: React.FC<React.PropsWithChildren<MiddleNodeProps>> = ({
  node,
  updateCheckedItems,
  updateSubCheckedItems,
  checkboxItems,
  indent,
}) => {
  return (
    <>
      {!isLeafNode(node) && (
        <MiddleNode
          key={node.id}
          node={node}
          updateCheckedItems={updateCheckedItems}
          updateSubCheckedItems={updateSubCheckedItems}
          checkboxItems={checkboxItems}
          indent={indent}
        />
      )}
      {isLeafNode(node) && (
        <LeafNode
          key={node.id}
          node={node}
          checked={checkboxItems.get(node.id)!}
          updateCheckedItems={updateCheckedItems}
          indent={indent}
        />
      )}
    </>
  );
};

const MiddleNode: React.FC<React.PropsWithChildren<MiddleNodeProps>> = ({
  node,
  updateCheckedItems,
  updateSubCheckedItems,
  checkboxItems,
  indent,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const checkboxState = useMemo(
    () => getTopOrMiddleNodeState(node.undernoder, checkboxItems),
    [node.undernoder, checkboxItems],
  );

  const rowIconType = useMemo<IconType>(
    () => (isExpanded ? IconType.CloseRowTriangle : IconType.OpenRow),
    [isExpanded],
  );

  return (
    <>
      <Grid
        container={true}
        className={classNames(css.middleNode, css[`middleNode${indent}`])}
        key={node.id}
        id={node.id}
      >
        <Grid
          item={true}
          xs={9}
          className={classNames(css.node, css[`indent${indent}`])}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Icon
            type={rowIconType}
            htmlId={node.id}
            className={css.toggleExpand}
          />
          <Spacer width={20 * indent} />
          <span className="middleNodeName">{node.navn}</span>
        </Grid>
        <Grid item={true} xs={3} className={css[`indent${indent}`]}>
          <Checkbox
            color="secondary"
            checked={checkboxState === TopOrMiddleNodeState.Checked}
            indeterminate={checkboxState === TopOrMiddleNodeState.Indeterminate}
            className="middleNodeCheckbox"
            onChange={(e) => updateSubCheckedItems(node, e.target.checked)}
          />
        </Grid>
      </Grid>
      {isExpanded &&
        node.undernoder.map((n) => (
          <MiddleOrLeafNode
            key={n.id}
            node={n}
            updateCheckedItems={updateCheckedItems}
            updateSubCheckedItems={updateSubCheckedItems}
            checkboxItems={checkboxItems}
            indent={indent + 1}
          />
        ))}
    </>
  );
};

interface LeafNodeProps {
  node: Node;
  updateCheckedItems: (id: string, checked: boolean) => void;
  checked: boolean;
  indent: number;
}

const LeafNode: React.FC<React.PropsWithChildren<LeafNodeProps>> = ({
  node,
  updateCheckedItems,
  checked,
  indent,
}) => {
  return (
    <Grid container={true} className={css.leafNode} key={node.id} id={node.id}>
      <Grid
        item={true}
        xs={9}
        className={classNames(css.node, css[`indent${indent}`])}
      >
        <Spacer width={20 * indent} />
        <Icon type={IconType.EndNode} htmlId={node.id} />{' '}
        <span className="leafNodeName">{node.navn}</span>
      </Grid>
      <Grid item={true} xs={3} className={css[`indent${indent}`]}>
        <Checkbox
          color="secondary"
          checked={checked}
          className="leafNodeCheckbox"
          onChange={(e) => {
            checked = e.target.checked;
            updateCheckedItems(node.id, checked);
          }}
        />
      </Grid>
    </Grid>
  );
};

export default PraksisstederWithAvtale;
